/**
 * Per-module workspace shells.
 *
 * Each of the 7 modules wraps its sub-routes in a shell that renders
 * <ModuleLayout> with the locked tab list from DESIGN_SYSTEM.md §1.
 * The shell renders the page header + tabs, then <Outlet /> swaps in
 * whichever sub-route matches.
 *
 * Mission Control intentionally has no shell — it's the only module
 * without sub-modules per §1.
 */

import {
  Activity,
  BarChart3,
  Settings as SettingsIcon,
  Settings2,
  Sparkles,
  Users,
} from "lucide-react";

import { ModuleLayout } from "./ModuleLayout";
import type { ModuleTab } from "./ui/ModuleTabs";

// People — DESIGN_SYSTEM.md §1: Overview · Employees · Positions ·
// Org Chart · Contracts · Jobs. Contracts list ships in M26 PR 3.
const PEOPLE_TABS: ModuleTab[] = [
  { labelKey: "nav.overview", to: "/people" },
  { labelKey: "nav.employees", to: "/people/employees" },
  { labelKey: "nav.positions", to: "/people/positions" },
  { labelKey: "nav.org_chart", to: "/people/org-chart" },
  { labelKey: "nav.contracts", to: "/people/contracts" },
  { labelKey: "nav.jobs", to: "/people/jobs" },
];

export function PeopleModule() {
  return (
    <ModuleLayout
      titleKey="nav.people"
      icon={Users}
      tabs={PEOPLE_TABS}
      ariaLabel="People sub-modules"
    />
  );
}

// Operations — DESIGN_SYSTEM.md §1: Overview · Approvals · Leave ·
// Loans · Excuses · Time & Attendance · End of Service · Payroll.
// The unified cross-workflow Approvals queue ships in M26 PR 3.
const OPERATIONS_TABS: ModuleTab[] = [
  { labelKey: "nav.overview", to: "/operations" },
  { labelKey: "nav.approvals", to: "/operations/approvals" },
  { labelKey: "nav.leave", to: "/operations/leave/requests" },
  { labelKey: "nav.loans", to: "/operations/loans/applications" },
  { labelKey: "nav.excuse_queue", to: "/operations/excuses/queue" },
  { labelKey: "nav.time_attendance", to: "/operations/attendance" },
  { labelKey: "nav.eos", to: "/operations/eos" },
  { labelKey: "nav.payroll", to: "/operations/payroll" },
  { labelKey: "nav.import", to: "/operations/import" },
];

export function OperationsModule() {
  return (
    <ModuleLayout
      titleKey="nav.operations"
      icon={Activity}
      tabs={OPERATIONS_TABS}
      ariaLabel="Operations sub-modules"
    />
  );
}

// Policies — DESIGN_SYSTEM.md §1: Overview · AI Ingestion · Grades ·
// Leave Types · Loan Products · Allowances · Excuse Types · Shifts ·
// Approval Chains · Holiday Calendar. AI Ingestion moved here from
// /ai/ingestion in M26 PR 2. Approval Chains ships in M21.13. Holiday
// Calendar ships in M26 PR 5.
const POLICIES_TABS: ModuleTab[] = [
  { labelKey: "nav.overview", to: "/policies" },
  { labelKey: "nav.ai_ingestion", to: "/policies/ai-ingestion" },
  { labelKey: "nav.grades", to: "/policies/grades" },
  { labelKey: "nav.leave_types", to: "/policies/leave-types" },
  { labelKey: "nav.loan_products", to: "/policies/loan-products" },
  { labelKey: "nav.allowances", to: "/policies/allowances" },
  { labelKey: "nav.excuse_types", to: "/policies/excuse-types" },
  { labelKey: "nav.shifts", to: "/policies/shifts" },
  { labelKey: "nav.approval_chains", to: "/policies/approval-chains" },
  { labelKey: "nav.holiday_calendar", to: "/policies/holiday-calendar" },
];

export function PoliciesModule() {
  return (
    <ModuleLayout
      titleKey="nav.policies"
      icon={Settings2}
      tabs={POLICIES_TABS}
      ariaLabel="Policies sub-modules"
    />
  );
}

// AI — DESIGN_SYSTEM.md §1: Overview · Activity Log · Behavior ·
// Boundaries · Persona · Knowledge · Sandbox · Stuck Inbox. M26 PR 2
// split AIConfigPage across 5 canonical deep-linkable URLs (one per
// tab). Activity Log is a redirect to the Audit Log filtered to AI
// actors. Knowledge is the M19 ingestion surface (now under
// /policies/ai-ingestion — the Knowledge tab inside AI Config is the
// PDF-upload-to-vault flow, a different surface). Sandbox is a
// "coming soon" placeholder inside AIConfigPage.
const AI_TABS: ModuleTab[] = [
  { labelKey: "nav.overview", to: "/ai" },
  { labelKey: "nav.stuck_inbox", to: "/ai/stuck-inbox" },
  { labelKey: "nav.behavior", to: "/ai/behavior" },
  { labelKey: "nav.boundaries", to: "/ai/boundaries" },
  { labelKey: "nav.persona", to: "/ai/persona" },
  { labelKey: "nav.knowledge", to: "/ai/knowledge" },
  { labelKey: "nav.sandbox", to: "/ai/sandbox" },
  { labelKey: "nav.activity_log", to: "/ai/activity-log" },
  // Credentials live with the agent that uses them. These deep-link
  // into Settings → Integrations with the matching card scrolled-to
  // and ring-highlighted (see IntegrationsList useScrollToHash).
  // Mudad / GOSI / Qiwa / Muqeem are government — they belong to
  // Operations / Compliance and land with M3X.GOV; not pre-added
  // here to avoid dead-end Rail 2 entries.
  { labelKey: "nav.ai_provider", to: "/settings/integrations#anthropic" },
  { labelKey: "nav.concierge_channel", to: "/settings/integrations#twilio" },
];

export function AIModule() {
  return (
    <ModuleLayout
      titleKey="nav.ai"
      icon={Sparkles}
      tabs={AI_TABS}
      ariaLabel="AI sub-modules"
    />
  );
}

// Reports — DESIGN_SYSTEM.md §1. Everything except Overview is
// deferred until the Reports module ships in a later milestone.
const REPORTS_TABS: ModuleTab[] = [
  { labelKey: "nav.overview", to: "/reports" },
  { labelKey: "nav.dashboards", to: "/reports/dashboards" },
  { labelKey: "nav.custom_builder", to: "/reports/custom-builder" },
  { labelKey: "nav.saved", to: "/reports/saved" },
  { labelKey: "nav.scheduled", to: "/reports/scheduled" },
  { labelKey: "nav.exports", to: "/reports/exports" },
];

export function ReportsModule() {
  return (
    <ModuleLayout
      titleKey="nav.reports"
      icon={BarChart3}
      tabs={REPORTS_TABS}
      ariaLabel="Reports sub-modules"
    />
  );
}

// Settings — DESIGN_SYSTEM.md §1: Overview · Tenant · Legal
// Entities · Users & Roles · Integrations · Notifications · Billing
// · Audit Log. Branding is added inline (custom-logo support).
// Users & Roles, Notifications, Audit Log ship as M21 shells (real
// routes); Tenant / Legal Entities / Billing are still deferred.
const SETTINGS_TABS: ModuleTab[] = [
  { labelKey: "nav.overview", to: "/settings" },
  // Setup tab — discoverable entry point to the M27 onboarding
  // journey. Lives under /settings (not /onboarding) so the
  // Settings module stays the active sidebar item while the user
  // is on this page. The journey itself is the same component
  // mounted at both /onboarding (legacy + widget links) and
  // /settings/setup (Settings tab + Unhide-widget action).
  { labelKey: "nav.setup", to: "/settings/setup" },
  { labelKey: "nav.branding", to: "/settings/branding" },
  { labelKey: "nav.integrations", to: "/settings/integrations" },
  { labelKey: "nav.users_roles", to: "/settings/users" },
  { labelKey: "nav.notifications", to: "/settings/notifications" },
  { labelKey: "nav.audit_log", to: "/settings/audit-log" },
  { labelKey: "nav.imports", to: "/settings/imports" },
  { labelKey: "nav.tenant", to: "/settings/tenant" },
  { labelKey: "nav.legal_entities", to: "/settings/legal-entities" },
  { labelKey: "nav.billing", to: "/settings/billing" },
];

export function SettingsModule() {
  return (
    <ModuleLayout
      titleKey="nav.settings"
      icon={SettingsIcon}
      tabs={SETTINGS_TABS}
      ariaLabel="Settings sub-modules"
    />
  );
}
